/**
 * Fetch with timeout using AbortController
 *
 * Wraps native fetch() with a configurable timeout to prevent indefinite hangs.
 * Throws TimeoutError when request exceeds the specified timeout.
 */

export class TimeoutError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'TimeoutError';
  }
}

export interface FetchWithTimeoutOptions extends RequestInit {
  timeout?: number; // milliseconds
}

/**
 * Fetch with automatic timeout
 *
 * @param url - URL to fetch
 * @param options - Fetch options plus optional timeout (default: 10000ms)
 * @returns Promise<Response>
 * @throws TimeoutError if request exceeds timeout
 *
 * @example
 * ```typescript
 * const response = await fetchWithTimeout(url, { timeout: 8000 });
 * ```
 */
export async function fetchWithTimeout(
  url: string,
  options: FetchWithTimeoutOptions = {}
): Promise<Response> {
  const { timeout = 10000, ...fetchOptions } = options;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...fetchOptions,
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);

    if (error instanceof Error && error.name === 'AbortError') {
      throw new TimeoutError(`Request timed out after ${timeout}ms`);
    }

    throw error;
  }
}
